import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutGrid, Landmark, Users, FileText, BookOpen, LogOut, ChevronDown, Compass, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/components/app/primitives";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const WORDMARK = "https://customer-assets.emergentagent.com/job_020b4353-d8b1-41a2-9f55-e68e3d0ed71c/artifacts/fsgsgk7e_Asset%203.png";

const ROLE_LABELS = {
  owner: "Owner", founder: "Founder",
  operating_partner: "Operating partner", advisor: "Advisor",
};
const TEAM = ["owner", "founder", "operating_partner"];
const FINANCE = ["owner", "founder"];

const NAV = [
  { to: "/", label: "Cockpit", icon: LayoutGrid, end: true, roles: "all", id: "cockpit" },
  { to: "/mission-control", label: "Mission Control", icon: Compass, roles: "all", id: "mission-control" },
  { to: "/capital", label: "Capital", icon: Landmark, roles: "team", id: "capital" },
  { to: "/people", label: "People and Org", icon: Users, roles: "team", id: "people" },
  { to: "/documents", label: "Documents", icon: FileText, roles: "all", id: "documents" },
  { to: "/admin", label: "Admin console", icon: ShieldCheck, roles: "finance", id: "admin" },
];

const PAGE_META = {
  "/": { title: "Cockpit", sub: "Where the studio stands today" },
  "/mission-control": { title: "Mission Control", sub: "Structure, teams, and venture command centers" },
  "/mission-control/digest": { title: "Weekly digest", sub: "This week across the studio" },
  "/admin": { title: "Admin console", sub: "Control teams, org, metrics, and access" },
  "/capital": { title: "Capital", sub: "Raise, pipeline, runway" },
  "/people": { title: "People and Org", sub: "Team, equity, work passes" },
  "/documents": { title: "Documents", sub: "Versioned, tagged by venture and arm" },
  "/documents/brand-system": { title: "Brand system", sub: "The studio voice and copy rules" },
};

function metaFor(pathname) {
  if (pathname.startsWith("/mission-control/venture/")) {
    return { title: "Command center", sub: "Month on month venture metrics" };
  }
  return PAGE_META[pathname] || { title: "Interlinked OS", sub: "" };
}

function initials(name = "") {
  return name.split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "IL";
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const meta = metaFor(location.pathname);
  const canSee = (roles) =>
    roles === "all" ||
    (roles === "team" && TEAM.includes(user?.role)) ||
    (roles === "finance" && FINANCE.includes(user?.role));

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[260px_1fr] bg-background">
      {/* Left rail */}
      <aside className="hidden lg:flex flex-col border-r border-border bg-[#0F1217] sticky top-0 h-screen">
        <div className="px-5 h-16 flex items-center border-b border-border">
          <img src={WORDMARK} alt="Interlinked Global" className="h-7 w-auto object-contain" />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          <p className="px-2 pb-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70">Areas</p>
          {NAV.filter((n) => canSee(n.roles)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-[rgba(43,79,216,0.14)] text-foreground border border-[rgba(43,79,216,0.35)]"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
              <span className="font-medium">{n.label}</span>
            </NavLink>
          ))}
          <div className="pt-4">
            <p className="px-2 pb-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70">Reference</p>
            <NavLink
              to="/documents/brand-system"
              data-testid="nav-brand-system"
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-[rgba(43,79,216,0.14)] text-foreground border border-[rgba(43,79,216,0.35)]"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <BookOpen className="h-[18px] w-[18px]" strokeWidth={1.75} />
              <span className="font-medium">Brand system</span>
            </NavLink>
          </div>
        </nav>
        <div className="px-4 py-3 border-t border-border">
          <StatusPill tone="accent" dot={false} label={ROLE_LABELS[user?.role] || "Member"} />
        </div>
      </aside>

      {/* Content column */}
      <div className="flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between gap-4 border-b border-border bg-background/85 backdrop-blur px-5 lg:px-6">
          <div className="min-w-0">
            <h1 className="font-display text-xl text-foreground leading-none truncate">{meta.title}</h1>
            {meta.sub && <p className="text-xs text-muted-foreground mt-1 truncate">{meta.sub}</p>}
          </div>
          <div className="flex items-center gap-3">
            <span
              data-testid="demo-data-badge"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-border bg-white/5 px-2.5 py-1 text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground"
            >
              Demo data, placeholder figures
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5 hover:bg-white/5 transition-colors duration-150">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[rgba(43,79,216,0.2)] text-[11px] font-semibold text-[#DCE6FF]">
                    {initials(user?.name)}
                  </span>
                  <span className="hidden sm:block text-sm text-foreground max-w-[140px] truncate">{user?.name}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm text-foreground">{user?.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">{user?.email}</span>
                    <span className="mt-2"><StatusPill tone="accent" dot={false} label={ROLE_LABELS[user?.role]} /></span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" strokeWidth={1.75} /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 px-5 lg:px-6 py-5 max-w-[1440px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
